import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Popover,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import reimbursementV2ApiService, {
  ReimbursementV2Request,
} from "../../services/reimbursement-v2-api";
import userManagementApiService from "../../services/user-management-api";
import taxClassificationApi, {
  TaxClassification,
} from "../../services/tax-classification-api";
import { Employee } from "../../types/leave";
import { formatDate } from "../../services/dateTimeService";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PdfViewer from "../../components/shared/PdfViewer";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/Info";

interface ReimbursementV2ProcessingPageProps {
  onStatusChange?: () => void;
}

/**
 * Reimbursement V2 Processing Page (Admin Only)
 * Allows admins to view, filter, search, and update status of all reimbursement V2 requests
 */
const ReimbursementV2ProcessingPage: React.FC<
  ReimbursementV2ProcessingPageProps
> = ({ onStatusChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState<ReimbursementV2Request[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<
    ReimbursementV2Request[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [taxClassifications, setTaxClassifications] = useState<
    TaxClassification[]
  >([]);

  // Helper function to get current month date range
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  const defaultDates = getDefaultDateRange();

  // Filter states - initialize with current month date range
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  // Temporary filter states for UI (not triggering API calls)
  const [tempSearchText, setTempSearchText] = useState("");
  const [tempStatusFilter, setTempStatusFilter] = useState("");
  const [tempEmployeeFilter, setTempEmployeeFilter] = useState("");
  const [tempStartDate, setTempStartDate] = useState(defaultDates.start);
  const [tempEndDate, setTempEndDate] = useState(defaultDates.end);

  // Status update popover
  const [statusPopoverAnchor, setStatusPopoverAnchor] =
    useState<HTMLElement | null>(null);
  const [selectedRequest, setSelectedRequest] =
    useState<ReimbursementV2Request | null>(null);
  const [newStatus, setNewStatus] = useState("");

  // Rejection comment dialog
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState("");
  const [rejectionError, setRejectionError] = useState("");

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>("");

  // Fetch requests with filters, employees, and tax classifications on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all employees for filter dropdown
        const allEmployees = await userManagementApiService.getAllUsers(
          0,
          1000,
        );
        setEmployees(allEmployees.content);

        // Fetch all tax classifications for mapping
        const classifications = await taxClassificationApi.getAll();
        setTaxClassifications(classifications);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t("error.failedToFetchEmployees");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize temp filters when component mounts
  useEffect(() => {
    setTempSearchText(searchText);
    setTempStatusFilter(statusFilter);
    setTempEmployeeFilter(employeeFilter);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, []);

  // Fetch requests with current filters
  useEffect(() => {
    const fetchFilteredRequests = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call server-side filtering endpoint
        const filteredRequests =
          await reimbursementV2ApiService.getReimbursementRequestsWithFilters(
            startDate || undefined,
            endDate || undefined,
            statusFilter || undefined,
            employeeFilter ? parseInt(employeeFilter) : undefined,
            searchText || undefined,
          );

        setRequests(filteredRequests);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t("error.failedToFetchRequests");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredRequests();
  }, [searchText, statusFilter, employeeFilter, startDate, endDate]);

  // Handle search button click
  const handleSearch = () => {
    setSearchText(tempSearchText);
    setStatusFilter(tempStatusFilter);
    setEmployeeFilter(tempEmployeeFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  // Handle reset button click
  const handleReset = () => {
    setTempSearchText("");
    setTempStatusFilter("");
    setTempEmployeeFilter("");
    setTempStartDate(defaultDates.start);
    setTempEndDate(defaultDates.end);
    setSearchText("");
    setStatusFilter("");
    setEmployeeFilter("");
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
  };

  // Check if temp filters differ from applied filters (for Apply button)
  const isDirty =
    tempSearchText !== searchText ||
    tempStatusFilter !== statusFilter ||
    tempEmployeeFilter !== employeeFilter ||
    tempStartDate !== startDate ||
    tempEndDate !== endDate;

  // Check if applied filters differ from default values (for Reset button)
  const isNotDefault =
    searchText !== "" ||
    statusFilter !== "" ||
    employeeFilter !== "" ||
    startDate !== defaultDates.start ||
    endDate !== defaultDates.end;

  // No need for client-side filtering anymore - server handles it
  // Just use requests directly as filteredRequests
  useEffect(() => {
    setFilteredRequests(requests);
  }, [requests]);

  const handleStatusChange = async (status: string) => {
    if (!selectedRequest) return;

    // If status is REJECTED, show rejection comment dialog
    if (status === "REJECTED") {
      setStatusPopoverAnchor(null);
      setRejectionDialogOpen(true);
      setRejectionComment("");
      setRejectionError("");
      return;
    }

    try {
      setLoading(true);
      await reimbursementV2ApiService.updateReimbursementRequestStatus(
        selectedRequest.requestId!,
        status,
        user?.username || "",
      );

      // Update local state
      setRequests(
        requests.map((req) =>
          req.requestId === selectedRequest.requestId
            ? { ...req, status: status }
            : req,
        ),
      );

      setStatusPopoverAnchor(null);
      setSelectedRequest(null);
      setNewStatus("");

      // Trigger badge refresh in parent component
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToUpdateStatus");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectionSubmit = async () => {
    if (!selectedRequest) return;

    // Validate rejection comment
    if (!rejectionComment.trim()) {
      setRejectionError(t("reimbursement.v2.rejectionCommentRequired"));
      return;
    }

    try {
      setLoading(true);
      await reimbursementV2ApiService.updateReimbursementRequestStatus(
        selectedRequest.requestId!,
        "REJECTED",
        user?.username || "",
        rejectionComment,
      );

      // Update local state
      setRequests(
        requests.map((req) =>
          req.requestId === selectedRequest.requestId
            ? { ...req, status: "REJECTED", rejectionComment: rejectionComment }
            : req,
        ),
      );

      setRejectionDialogOpen(false);
      setSelectedRequest(null);
      setNewStatus("");
      setRejectionComment("");
      setRejectionError("");

      // Trigger badge refresh in parent component
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToUpdateStatus");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFiles = async (request: ReimbursementV2Request) => {
    try {
      // Download PDF if available
      if (request.attachmentFilePath) {
        const attachmentFileName = request.attachmentFilePath.replace(
          /\.[^/.]+$/,
          "",
        );

        const pdfResponse = await fetch(
          `/ofc/reimbursement/v2/download/pdf/${request.requestId}`,
        );
        if (!pdfResponse.ok) {
          throw new Error(`Failed to download PDF: ${pdfResponse.statusText}`);
        }
        const pdfBlob = await pdfResponse.blob();
        const pdfUrl = window.URL.createObjectURL(pdfBlob);
        const pdfLink = document.createElement("a");
        pdfLink.href = pdfUrl;
        // Use X-Filename header if available, otherwise use requestId
        const pdfFileName = `${attachmentFileName}.pdf`;
        pdfLink.download = pdfFileName;
        document.body.appendChild(pdfLink);
        pdfLink.click();
        window.URL.revokeObjectURL(pdfUrl);
        document.body.removeChild(pdfLink);

        // Download CSV if available
        const csvResponse = await fetch(
          `/ofc/reimbursement/v2/download/csv/${request.requestId}`,
        );
        if (!csvResponse.ok) {
          throw new Error(`Failed to download CSV: ${csvResponse.statusText}`);
        }
        const csvBlob = await csvResponse.blob();
        const csvUrl = window.URL.createObjectURL(csvBlob);
        const csvLink = document.createElement("a");
        csvLink.href = csvUrl;
        // Use X-Filename header if available, otherwise use requestId
        const csvFileName = `${attachmentFileName}.csv`;
        csvLink.download = csvFileName;
        document.body.appendChild(csvLink);
        csvLink.click();
        window.URL.revokeObjectURL(csvUrl);
        document.body.removeChild(csvLink);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToDownloadFiles");
      setError(errorMessage);
    }
  };

  const handleViewAttachment = (request: ReimbursementV2Request) => {
    try {
      const pdfUrl = `/ofc/reimbursement/v2/download/pdf/${request.requestId}`;
      setSelectedPdfUrl(pdfUrl);
      setSelectedPdfFileName(`${request.requestId}_attachment.pdf`);
      setPdfViewerOpen(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToViewAttachment");
      setError(errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUBMITTED":
        return "info";
      case "PROCESSING":
        return "warning";
      case "PROCESSED":
        return "success";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const getEmployeeName = (employeeId?: number) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : "Unknown";
  };

  const getTaxClassificationName = (taxClassificationId?: number): string => {
    if (!taxClassificationId) return "-";
    const taxClassification = taxClassifications.find(
      (tc) => tc.id === taxClassificationId,
    );
    if (!taxClassification) return "-";

    // Format: "Display Text (Tax%)" or just "Display Text" if no tax percent
    return `${taxClassification.displayText}${taxClassification.taxPercent ? ` (${taxClassification.taxPercent}%)` : ""}`;
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("reimbursement.v2.processing.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              label={t("users.startDate")}
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              label={t("leave.endDate")}
              type="date"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("common.status")}</InputLabel>
              <Select
                value={tempStatusFilter}
                onChange={(e) => setTempStatusFilter(e.target.value)}
                label={t("common.status")}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                <MenuItem value="PROCESSING">PROCESSING</MenuItem>
                <MenuItem value="PROCESSED">PROCESSED</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("leave.employee")}</InputLabel>
              <Select
                value={tempEmployeeFilter}
                onChange={(e) => setTempEmployeeFilter(e.target.value)}
                label={t("leave.employee")}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id?.toString()}>
                    {emp.firstName} {emp.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <TextField
              fullWidth
              placeholder={t("common.search")}
              value={tempSearchText}
              onChange={(e) => setTempSearchText(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 6, md: 1.5 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!isDirty}
              size="small"
            >
              {t("common.apply")}
            </Button>
            <Tooltip title={t("common.reset")}>
              <span>
                <IconButton
                  onClick={handleReset}
                  disabled={!isNotDefault}
                  color="primary"
                  size="small"
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("leave.employee")}</TableCell>
                <TableCell>{t("reimbursement.v2.submissionDate")}</TableCell>
                <TableCell>{t("reimbursements.vendor")}</TableCell>
                <TableCell>{t("reimbursements.item")}</TableCell>
                <TableCell>{t("reimbursement.v2.taxClassification")}</TableCell>
                <TableCell align="center">
                  {t("reimbursement.v2.amount")}
                </TableCell>
                <TableCell align="center">{t("common.status")}</TableCell>
                <TableCell align="center">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{getEmployeeName(request.employeeId)}</TableCell>
                  <TableCell>
                    {request.createdAt ? formatDate(request.createdAt) : "-"}
                  </TableCell>
                  <TableCell>{request.vendor}</TableCell>
                  <TableCell>{request.itemDetails}</TableCell>
                  <TableCell>
                    {getTaxClassificationName(request.taxClassificationId)}
                  </TableCell>
                  <TableCell align="center">
                    ¥{request.totalAmount?.toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status!) as any}
                        size="small"
                        onClick={(e) => {
                          setSelectedRequest(request);
                          setStatusPopoverAnchor(e.currentTarget);
                        }}
                        sx={{ cursor: "pointer" }}
                      />
                      {request.status === "REJECTED" &&
                        request.rejectionComment && (
                          <Tooltip title={request.rejectionComment}>
                            <InfoIcon
                              sx={{
                                fontSize: 18,
                                color: "error.main",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("common.view")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleViewAttachment(request)}
                          disabled={!request.attachmentFilePath}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        <>
                          {`${request.attachmentFilePath?.replace(/\.[^/.]+$/, "")} (.pdf, .csv)`}
                        </>
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadFiles(request)}
                        disabled={
                          !request.attachmentFilePath && !request.csvFilePath
                        }
                        color="primary"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Status Update Popover */}
      <Popover
        open={Boolean(statusPopoverAnchor)}
        anchorEl={statusPopoverAnchor}
        onClose={() => {
          setStatusPopoverAnchor(null);
          setSelectedRequest(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
            {t("reimbursement.v2.processing.currentStatus")}:{" "}
            {selectedRequest?.status}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {["SUBMITTED", "PROCESSING", "PROCESSED", "REJECTED"].map(
              (status) => (
                <Button
                  key={status}
                  size="small"
                  variant={
                    selectedRequest?.status === status
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() => handleStatusChange(status)}
                  disabled={loading}
                  fullWidth
                >
                  {status}
                </Button>
              ),
            )}
          </Box>
        </Box>
      </Popover>

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={selectedPdfUrl}
        fileName={selectedPdfFileName}
      />

      {/* Rejection Comment Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => {
          setRejectionDialogOpen(false);
          setRejectionComment("");
          setRejectionError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("reimbursement.v2.rejectWithComment")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("reimbursement.v2.rejectionComment")}
            placeholder={t("reimbursement.v2.rejectionCommentPlaceholder")}
            fullWidth
            multiline
            rows={4}
            value={rejectionComment}
            onChange={(e) => {
              setRejectionComment(e.target.value);
              setRejectionError("");
            }}
            error={!!rejectionError}
            helperText={rejectionError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectionDialogOpen(false);
              setRejectionComment("");
              setRejectionError("");
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleRejectionSubmit}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {t("common.reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReimbursementV2ProcessingPage;
